import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { PerformanceTimingManager } from '@databases/frontend-metrics/performance-timing-manager';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { doAddEntry } from '@databases/redux/table-layout/actions';
import { getService } from '@databases/service-container';
import { type DatabaseEntry } from '@databases/types/database';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import useHasNumberedEntries from './UseHasNumberedEntries';

const StyledButton = styled(Button)`
  border-radius: 0;

  && {
    align-items: center;
    height: 100%;
    text-align: left;
  }

  svg {
    height: 18px;
    width: auto;
  }
`;

const addEntryContainer = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderBottomLeftRadius: 'radius.small',
  borderBottomRightRadius: 'radius.small',
  borderColor: 'color.border',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
});

const addEntryContainerFaded = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderTopWidth: 'border.width',
  borderLeftWidth: 'border.width',
  borderRightWidth: 'border.width',
  borderBottomWidth: 'border.width.0',
  borderStyle: 'solid',
  borderBottomLeftRadius: 'radius.small',
  borderBottomRightRadius: 'radius.small',
  borderColor: 'color.border.disabled',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
});

const addEntryContainerStylesWithNumberedEntries = xcss({
  borderBottomLeftRadius: '0',
});

const visualRefreshAddEntryContainerStyles = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderBottomLeftRadius: 'radius.small',
  borderBottomRightRadius: 'radius.small',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
  borderTop: 'none',
  borderBottom: 'none',
  background: `
    linear-gradient(to right, transparent 0%, ${token('color.border')} 3%, ${token(
    'color.border'
  )} 98%, transparent 100%) top,
      linear-gradient(to right, transparent 0%, ${token('color.border')} 3%, ${token(
    'color.border'
  )} 98%, transparent 100%) bottom
  `,
  backgroundSize: '100% 1px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'top, bottom',
});

const visualRefreshAddEntryContainerStylesFaded = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderBottomLeftRadius: 'radius.small',
  borderBottomRightRadius: 'radius.small',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
  borderTop: 'none',
  borderBottom: 'none',
  background: `
    linear-gradient(to right, transparent 0%, ${token(
      'color.border.disabled'
    )} 3%, ${token('color.border.disabled')} 98%, transparent 100%) top
  `,
  backgroundSize: '100% 1px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'top',
});

export interface AddEntryButtonProps {
  addEntryButtonRef: React.RefObject<HTMLButtonElement>;
  contentId: string;
  onAdd?: (entry: DatabaseEntry) => void;
  width: number | undefined;
  isFaded?: boolean;
}

const AddEntryButton: FunctionComponent<AddEntryButtonProps> = ({
  addEntryButtonRef,
  contentId,
  onAdd,
  width,
  isFaded = false,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const client = useAnalytics();
  const {
    view: { settings: viewSettings },
  } = useView();
  const { formatMessage } = useIntl();

  const hasNumberedEntries = useHasNumberedEntries({ contentId });
  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );

  const handleClick = useCallback(async () => {
    const reliability = ReliabilityMetrics.getInstance();

    try {
      const clickTime = performance.now();

      selectionsActions.deselectAll();

      client.sendAddEntryButtonClickedEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'tableLayout',
      });
      const entry = dispatch(doAddEntry(contentId, viewSettings));
      PerformanceTimingManager.startMetricTiming({
        metricName: MetricName.DATABASE_ADD_ENTRY_TIME,
        id: entry.id,
        startTime: clickTime,
      });
      onAdd?.(entry);
      reliability.success(MetricName.DATABASE_ADD_ENTRY);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      reliability.failure(MetricName.DATABASE_ADD_ENTRY, {
        errorMessage,
      });
      throw e;
    }
  }, [client, contentId, dispatch, onAdd, selectionsActions, viewSettings]);

  const stickyStyles = xcss({
    position: 'sticky',
    bottom: 'space.0',
    left: 'space.0',
    zIndex: 'blanket',
    backgroundColor: 'elevation.surface',
  });

  return (
    <Flex xcss={[stickyStyles, xcss({ width: '100%' })]}>
      <Box
        xcss={[
          isVisualRefreshM1Enabled
            ? isFaded
              ? visualRefreshAddEntryContainerStylesFaded
              : visualRefreshAddEntryContainerStyles
            : isFaded
            ? addEntryContainerFaded
            : addEntryContainer,
          ...(hasNumberedEntries ? [addEntryContainerStylesWithNumberedEntries] : []),
        ]}
        style={{
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          marginRight: `0`,
          width: width ? width : '100%',
        }}
      >
        <StyledButton
          ref={addEntryButtonRef}
          appearance="subtle"
          onClick={handleClick}
          iconBefore={
            <AddIcon
              spacing="spacious"
              color={
                isFaded
                  ? token('color.text.disabled', '#8993A4')
                  : token('color.text.subtlest', '#626F86')
              }
              label=""
            />
          }
          spacing="compact"
          shouldFitContainer
          testId="table-layout-add-entry"
        >
          <Text
            color={isFaded ? 'color.text.disabled' : 'color.text.subtlest'}
            size={'medium'}
          >
            {formatMessage(messages['table-layout.add-entry'])}
          </Text>
        </StyledButton>
      </Box>
    </Flex>
  );
};

export default AddEntryButton;
